import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Phone, 
  MessageCircle, 
  CalendarCheck, 
  HeartHandshake,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { howItWorks } from '../data/mock';

const stepIcons = [Phone, MessageCircle, CalendarCheck, HeartHandshake];

const HowItWorks = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section 
        className="py-20"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <motion.h1
              className="heading-hero mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              How It Works
            </motion.h1>
            <motion.p
              className="body-large mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Getting help for yourself or your loved ones is simple. 
              No complicated processes, no apps to download — just human support when you need it.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Steps */}
            <div className="space-y-8">
              {howItWorks.map((step, index) => {
                const IconComponent = stepIcons[index];
                return (
                  <motion.div
                    key={step.step}
                    className="flex gap-6"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 rounded-full bg-[#232323] flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-sm text-[#666] uppercase tracking-wide mb-1">
                        Step {step.step}
                      </div>
                      <h3 className="heading-3 mb-2">{step.title}</h3>
                      <p className="body-small">{step.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Right - Benefits Card */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-sm h-fit lg:sticky lg:top-32"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="heading-2 mb-6">What Makes Us Different</h3>
              <ul className="space-y-4">
                {[
                  'No app download or login required',
                  'Speak to a real person, not a chatbot',
                  'Pricing discussed and confirmed upfront',
                  'Pay only after the service is completed',
                  'Family members can request on behalf of seniors',
                  'Regular updates keep everyone informed'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4caf50] flex-shrink-0 mt-0.5" />
                    <span className="text-[#353535]">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-[#eee]">
                <Link to="/request-help" className="btn-primary w-full gap-2">
                  <Phone className="w-4 h-4" />
                  Request Help Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <motion.h2
            className="heading-1 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Have Questions?
          </motion.h2>
          <motion.p
            className="body-large mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Check our pricing page for common questions or reach out directly — 
            we're happy to help.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/pricing" className="btn-secondary gap-2">
              View Pricing Info
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary gap-2">
              Contact Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;